/**
 * Screen names ⇄ URLs, for the agent.
 *
 * The agent speaks in screens and params (`FundDeepDive`, `{ id, tab }`); the
 * app speaks in paths and query strings. Everything that crosses between the
 * two goes through here, so navigation, the run requests and the context sent
 * with each question cannot disagree about what a URL means.
 *
 * Pure: no React, no router, no store. The catalogue (`discovery.js`) is the
 * only source — a screen, a param or a value that is not declared there does
 * not exist as far as the agent is concerned, which is what keeps a
 * hallucinated tab from becoming a URL the page silently ignores.
 */
import {
  SCREENS, isListParam, paramValues, screenForward, screenSeats,
} from './discovery';
import { isScreenActive, ROUTES } from '../../config/navigation';
import { normOption, resolveOption } from './options';
import { isReportAddress } from '../reportUrl';

const has = (key) => typeof key === 'string' && Object.prototype.hasOwnProperty.call(SCREENS, key);

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/** Nothing the page could read: absent, an empty string, or an empty list. */
const isBlank = (v) => v == null
  || (typeof v === 'string' && v.trim() === '')
  || (Array.isArray(v) && v.length === 0);

/** A single URL-safe value as text, or null for anything that is not one (objects, NaN). */
const scalar = (v) => {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : null;
  if (typeof v === 'boolean') return String(v);
  return null;
};

const safeDecode = (s) => {
  try {
    return decodeURIComponent(s);
  } catch {
    /* A malformed escape in a hand-typed URL is still the value the router saw. */
    return s;
  }
};

/** Path without query, hash or trailing slash — the form the nav matchers compare. */
const cleanPath = (pathname) => {
  const path = String(pathname).split(/[?#]/)[0];
  return path.length > 1 ? path.replace(/\/+$/, '') || '/' : path;
};

/** A path segment the URL parser would fold away rather than keep. */
const DOT_SEGMENT = /^\.{1,2}$/;

/** The "one of" tail of a refusal, kept short enough to survive the report's 300-char cap. */
const listed = (values) => {
  const list = values.join(', ');
  return list.length > 160 ? `${list.slice(0, 157)}…` : list;
};

/* ── name resolution ─────────────────────────────────────────────────────── */

/**
 * Normalised name → screen key, built once: the key (`fund`) and the agent
 * name (`FundDeepDive`) are both accepted.
 */
const LOOKUP = (() => {
  const byName = new Map();
  Object.entries(SCREENS).forEach(([key, entry]) => {
    byName.set(normOption(key), key);
    byName.set(normOption(entry.screen), key);
  });
  return byName;
})();

/**
 * The screen key for a screen name, key or app path — or null.
 *
 * Case, spaces, underscores and hyphens are ignored, so `fund_deep_dive`,
 * `Fund Deep Dive` and `FundDeepDive` all land on `fund`. Nothing looser than
 * that: a React Native screen name leaking through a handoff must not be
 * fuzzy-matched into a navigation nobody asked for.
 */
export function resolveScreen(nameOrKey) {
  if (nameOrKey == null) return null;
  const raw = String(nameOrKey).trim();
  if (!raw) return null;
  if (has(raw)) return raw;
  if (raw.startsWith('/')) return screenForPath(raw);
  const n = normOption(raw);
  return n ? LOOKUP.get(n) ?? null : null;
}

/* ── building a URL ──────────────────────────────────────────────────────── */

/** The items of a list value: an array, or comma-separated text. Null when an item is not a scalar. */
function listItems(raw) {
  if (Array.isArray(raw)) {
    const items = raw.map(scalar);
    return items.some((item) => item == null) ? null : [...new Set(items.filter(Boolean))];
  }
  const text = scalar(raw);
  return text == null ? null : [...new Set(text.split(',').map((s) => s.trim()).filter(Boolean))];
}

/**
 * One query param's value as the URL carries it, or `{ bad }` naming why not.
 *
 * An enumerated value resolves to its id ("Risk" → `risk`). A list param
 * (`horizons=1y,3y`) takes an array or comma-separated text, each item
 * resolved when the list is enumerated, and is written comma-joined.
 */
function queryValue(key, name, raw) {
  const values = paramValues(key, name);
  if (isListParam(key, name) || Array.isArray(raw)) {
    const items = listItems(raw);
    if (!items) return { bad: 'expected text or a list of text' };
    const ids = values ? items.map((item) => resolveOption(item, values)) : items;
    if (ids.some((id) => id == null)) return { bad: 'values' };
    return { value: [...new Set(ids.map(String))].join(',') };
  }
  const text = scalar(raw);
  if (text == null) return { bad: `expected text, not ${typeof raw}` };
  if (!values) return { value: text };
  const id = resolveOption(text, values);
  return id == null ? { bad: 'values' } : { value: String(id) };
}

/** "FundDeepDive needs id (string — REQUIRED. securitySymbol of …)" — the prose tells the agent what to send. */
const needs = (entry, name) => {
  const why = (entry.params[name] || '').replace(/\.$/, '');
  const reason = `${entry.screen} needs ${name}${why ? ` (${why})` : ''}`;
  return reason.length > 280 ? `${reason.slice(0, 279)}…` : reason;
};

/**
 * The URL for a screen with params, or the reason there cannot be one.
 *
 * `{ ok: true, pathname, search, routeParams, queryParams }` or
 * `{ ok: false, code: 'unknown_screen' | 'missing_param', reason }`.
 *
 * Route params fill the `:name` segments (encoded). Declared query params go
 * to the search string in catalogue order; an enumerated one is resolved to its
 * id ("Risk" → `risk`) and refused when nothing matches, with the values that
 * would, and a list param is written comma-separated. Params the screen does
 * not declare are dropped rather than refused:
 * agents routinely send the whole subject along, and a URL carrying keys no
 * page reads would only make two identical screens look different.
 *
 * A screen that only forwards (`clients`) answers for where it lands, so the
 * location the caller waits for is the one the router will actually show.
 */
export function pathFor(key, params = {}) {
  const resolved = has(key) ? key : resolveScreen(key);
  const entry = resolved ? SCREENS[resolved] : null;
  if (!entry) {
    return { ok: false, code: 'unknown_screen', reason: `unknown screen ${JSON.stringify(String(key ?? ''))}` };
  }
  const given = isPlainObject(params) ? params : {};

  const forward = screenForward(resolved);
  if (forward && has(forward.screen) && !screenForward(forward.screen)) {
    /* What the old screen required, it still requires: StockDetail without a
       symbol is nothing to show, wherever it lands. */
    for (const name of entry.routeParams) {
      if (isBlank(given[name])) return { ok: false, code: 'missing_param', reason: needs(entry, name) };
    }
    /* The old screen's params under the names the new one reads (`sym` →
       `symbol`); a param the map does not name travels as it is. */
    const carried = {};
    Object.entries(given).forEach(([name, value]) => {
      carried[forward.map?.[name] || name] = value;
    });
    return pathFor(forward.screen, { ...forward.params, ...carried });
  }

  const routeParams = {};
  const segments = [];
  for (const segment of entry.route.split('/')) {
    if (!segment.startsWith(':')) {
      segments.push(segment);
      continue;
    }
    const name = segment.slice(1);
    if (isBlank(given[name])) {
      return { ok: false, code: 'missing_param', reason: needs(entry, name) };
    }
    const text = scalar(given[name]);
    if (!text) {
      return { ok: false, code: 'missing_param', reason: `invalid ${name} — ${entry.screen} needs it as text` };
    }
    /* `encodeURIComponent` leaves dots alone, and the browser folds a '.' or
       '..' segment into the path around it: `/funds/..` would open `/`, a
       screen the seat check never saw. */
    if (DOT_SEGMENT.test(text)) {
      return { ok: false, code: 'missing_param', reason: `invalid ${name} "${text}" — ${entry.screen} needs a real ${name}` };
    }
    routeParams[name] = text;
    segments.push(encodeURIComponent(text));
  }

  const search = new URLSearchParams();
  const queryParams = {};
  for (const name of entry.queryParams) {
    const raw = given[name];
    if (isBlank(raw)) continue;
    const { value, bad } = queryValue(resolved, name, raw);
    if (bad === 'values') {
      const shown = Array.isArray(raw) ? raw.join(',') : String(scalar(raw) ?? '');
      return {
        ok: false,
        code: 'missing_param',
        reason: `invalid ${name} "${shown.slice(0, 60)}" — one of: ${listed(paramValues(resolved, name))}`,
      };
    }
    if (bad) return { ok: false, code: 'missing_param', reason: `invalid ${name} — ${bad}` };
    if (!value) continue;
    if (resolved === 'reportView' && name === 'url' && !isReportAddress(value)) {
      return {
        ok: false,
        code: 'missing_param',
        reason: 'invalid url — ReportViewer needs the absolute http(s) address of a report',
      };
    }
    search.set(name, value);
    queryParams[name] = value;
  }

  return {
    ok: true,
    pathname: segments.join('/') || '/',
    /* Commas unescaped: a list reads `funds=a,b` in the address bar, as the pages write it. */
    search: search.toString().replace(/%2C/gi, ','),
    routeParams,
    queryParams,
  };
}

/* ── reading a URL ───────────────────────────────────────────────────────── */

/**
 * The screen key owning a path, or null.
 *
 * Uses the nav config's own matchers, so the agent and the seat guard agree on
 * which screen `/funds/X` is. The report viewer has no nav id, so it is matched
 * here by its route.
 */
export function screenForPath(pathname) {
  if (typeof pathname !== 'string' || !pathname) return null;
  const path = cleanPath(pathname);
  if (has('reportView') && path === SCREENS.reportView.route) return 'reportView';
  return Object.keys(ROUTES).find((key) => has(key) && isScreenActive(key, path)) ?? null;
}

/**
 * What a URL says about a screen: its route params, decoded, and the declared
 * query params it carries.
 *
 * Read the way the page reads them — an enumerated value resolves to its id and
 * one nothing matches is left out, because the page falls back to its default
 * for it and the agent should be told what is on screen, not what was typed.
 * A list param comes back as an array.
 */
export function paramsFor(key, pathname, search) {
  if (!has(key) || typeof pathname !== 'string') return {};
  const entry = SCREENS[key];
  const out = {};

  const routeSegments = entry.route.split('/');
  const pathSegments = cleanPath(pathname).split('/');
  routeSegments.forEach((segment, i) => {
    if (segment.startsWith(':') && pathSegments[i]) out[segment.slice(1)] = safeDecode(pathSegments[i]);
  });

  let query;
  if (search instanceof URLSearchParams) query = search;
  else query = new URLSearchParams(typeof search === 'string' ? search.replace(/^\?/, '') : '');

  entry.queryParams.forEach((name) => {
    const raw = query.get(name);
    if (raw == null || raw.trim() === '') return;
    const values = paramValues(key, name);
    if (isListParam(key, name)) {
      const items = raw.split(',').map((s) => s.trim()).filter(Boolean)
        .map((item) => (values ? resolveOption(item, values) : item))
        .filter((item) => item != null)
        .map(String);
      if (items.length) out[name] = [...new Set(items)];
      return;
    }
    if (!values) {
      out[name] = raw.trim();
      return;
    }
    const id = resolveOption(raw, values);
    if (id != null) out[name] = String(id);
  });

  return out;
}

/* ── seats ───────────────────────────────────────────────────────────────── */

/**
 * May this seat open this screen?
 *
 * The same answer the shell's guard gives (`screenSeats`, from
 * `allowedScreens`), checked before navigating so a refusal is reported instead
 * of bounced to the landing page.
 */
export function seatAllows(seat, key) {
  const resolved = has(key) ? key : resolveScreen(key);
  if (!resolved || seat == null) return false;
  return screenSeats(resolved).includes(seat);
}
